import { z } from "zod";

const nullableString = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable();
const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable();
const dateTimeSchema = z.string().datetime().nullable();
const weeklyCapacityHoursSchema = z.number().int().min(0).max(168).nullable();
const allocationPercentSchema = z.number().int().min(0).max(100);

export const specialistAvailabilityStatuses = [
  "available",
  "available_from",
  "unavailable",
  "not_looking",
] as const;

export const specialistAvailabilityStatusSchema = z.enum(specialistAvailabilityStatuses);

export const specialistAvailabilitySchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  availabilityStatus: specialistAvailabilityStatusSchema,
  availableFrom: z.string().nullable(),
  weeklyCapacityHours: z.number().nullable(),
  currentAllocationPercent: z.number(),
  lastConfirmedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  updatedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

function validateAvailability(
  value: {
    availabilityStatus?: string | undefined;
    availableFrom?: string | null | undefined;
  },
  context: z.RefinementCtx,
) {
  if (value.availabilityStatus === "available_from" && value.availableFrom === null) {
    context.addIssue({
      code: "custom",
      path: ["availableFrom"],
      message: "availableFrom is required when availabilityStatus is available_from",
    });
  }
}

export const specialistAvailabilityPutRequestSchema = z
  .object({
    availabilityStatus: specialistAvailabilityStatusSchema.default("available"),
    availableFrom: dateOnlySchema.default(null),
    weeklyCapacityHours: weeklyCapacityHoursSchema.default(null),
    currentAllocationPercent: allocationPercentSchema.default(0),
    lastConfirmedAt: dateTimeSchema.default(null),
    notes: nullableString(4_000).default(null),
  })
  .strict()
  .superRefine(validateAvailability);

export const specialistAvailabilityPatchRequestSchema = z
  .object({
    availabilityStatus: specialistAvailabilityStatusSchema.optional(),
    availableFrom: dateOnlySchema.optional(),
    weeklyCapacityHours: weeklyCapacityHoursSchema.optional(),
    currentAllocationPercent: allocationPercentSchema.optional(),
    lastConfirmedAt: dateTimeSchema.optional(),
    notes: nullableString(4_000).optional(),
  })
  .strict()
  .superRefine(validateAvailability);

export const specialistAvailabilityResponseSchema = z.object({
  item: specialistAvailabilitySchema.nullable(),
});

export type SpecialistAvailabilityStatus = z.infer<typeof specialistAvailabilityStatusSchema>;
export type SpecialistAvailability = z.infer<typeof specialistAvailabilitySchema>;
export type SpecialistAvailabilityPutRequest = z.infer<
  typeof specialistAvailabilityPutRequestSchema
>;
export type SpecialistAvailabilityPatchRequest = z.infer<
  typeof specialistAvailabilityPatchRequestSchema
>;
export type SpecialistAvailabilityResponse = z.infer<typeof specialistAvailabilityResponseSchema>;
